import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Clock, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import PageHero from "@/components/PageHero";
import CTABanner from "@/components/CTABanner";
import WhyChooseTraining from "@/components/WhyChooseTraining";
import WhoShouldAttend from "@/components/WhoShouldAttend";
import WhatYouLearn from "@/components/WhatYouLearn";
import TrainingOptions from "@/components/TrainingOptions";

const courses = [
  {
    id: "autocad-api",
    name: "AutoCAD APIs",
    description: "Master AutoCAD automation using .NET API, AutoLISP, and ObjectARX. Learn to build custom commands, plugins, and automate drawing tasks using C#, VB.NET, and Python.",
    duration: "2-3 months",
    level: "Intermediate to Advanced",
    technologies: ["C#", ".NET", "AutoLISP", "Python"]
  },
  {
    id: "solidworks-api",
    name: "SolidWorks APIs",
    description: "Learn SolidWorks API programming for automation and customization. Build add-ins, macros, and standalone applications using C#, VB.NET, and VBA.",
    duration: "2-3 months",
    level: "Intermediate to Advanced",
    technologies: ["C#", "VB.NET", "VBA", "Python"]
  },
  {
    id: "nx-open",
    name: "NX Open",
    description: "Master Siemens NX Open API for automation and customization. Develop custom applications, journaling, and integrate with external systems using C++, C#, and Python.",
    duration: "3-4 months",
    level: "Intermediate to Advanced",
    technologies: ["C++", "C#", "Python", "VB.NET"]
  },
  {
    id: "eyeshot-api",
    name: "devDept Eyeshot APIs",
    description: "Learn to build CAD applications using devDept Eyeshot library. Create 3D viewers, editors, and custom CAD solutions for .NET applications.",
    duration: "2-3 months",
    level: "Intermediate to Advanced",
    technologies: ["C#", ".NET", "WPF", "WinForms"]
  },
  {
    id: "inventor-api",
    name: "Inventor APIs",
    description: "Automate Autodesk Inventor with API programming. Build iLogic rules, add-ins, and custom tools using VB.NET, C#, and Python.",
    duration: "2-3 months",
    level: "Intermediate to Advanced",
    technologies: ["C#", "VB.NET", "iLogic", "Python"]
  },
  {
    id: "rhino3d-api",
    name: "Rhino 3D APIs",
    description: "Master Rhino 3D automation using RhinoCommon and Grasshopper APIs. Build plugins, scripts, and custom tools using C#, Python, and RhinoScript.",
    duration: "2-3 months",
    level: "Intermediate to Advanced",
    technologies: ["C#", "Python", "RhinoCommon", "Grasshopper"]
  },
  {
    id: "cad-automation",
    name: "CAD Automation",
    description: "Comprehensive course covering automation across multiple CAD platforms. Learn common patterns, best practices, and cross-platform development techniques.",
    duration: "3-4 months",
    level: "Advanced",
    technologies: ["C#", "Python", "C++", "APIs"]
  }
];


const CoursesPage = () => {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      
      <PageHero
        title="CAD Automation (APIs) Training Programs"
        subtitle="Industry-focused courses on CAD automation, customization, and API development. Learn to build powerful tools using C#, Python, C++, and more."
      >
        <div className="mb-6">
          <Breadcrumb />
        </div>
      </PageHero>

      {/* Course Grid */}
      <section data-testid="courses-grid" className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course) =>
            <div
              key={course.id}
              data-testid={`course-card-${course.id}`}
              className="course-card bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-[#06B6D4]/30 hover:shadow-lg transition-all duration-300">

                {/* Course Image Header */}
                <div className="h-36 overflow-hidden bg-slate-100">
                  <img
                  src="https://images.pexels.com/photos/716661/pexels-photo-716661.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt={`${course.name} blueprint`}
                  className="w-full h-full object-cover grayscale opacity-70 mix-blend-multiply" />

                </div>

                {/* Content */}
                <div className="p-6">
                  <h3
                  className="text-lg font-bold text-[#0F172A] mb-2"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}>

                    {course.name}
                  </h3>
                  
                  <p className="text-[#0F172A]/60 text-sm leading-relaxed mb-4">
                    {course.description}
                  </p>

                  {/* Technologies */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {course.technologies.map((tech, index) =>
                  <span
                    key={index}
                    className="px-2 py-0.5 bg-[#2563EB]/10 text-[#2563EB] rounded text-xs font-medium">

                        {tech}
                      </span>
                  )}
                  </div>

                  <div className="flex items-center gap-4 mb-4 text-sm">
                    <span className="flex items-center gap-1.5 text-[#0F172A]/60">
                      <Clock className="w-4 h-4 text-[#06B6D4]" />
                      {course.duration}
                    </span>
                  </div>

                  <Link to={`/courses/${course.id}`}>
                    <Button
                    data-testid={`view-course-${course.id}`}
                    className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl group">

                      View Details
                      <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why Choose Our Training */}
      <WhyChooseTraining />

      {/* Who Should Attend */}
      <WhoShouldAttend />

      {/* What You'll Learn */}
      <WhatYouLearn />

      {/* Training Options */}
      <TrainingOptions />

      {/* CTA Banner */}
      <CTABanner
        title="Not sure which course is right for you?"
        subtitle="Our experts can help you choose the perfect learning path based on your goals and experience level."
        buttonText="Contact Us"
        buttonLink="/contact" />


      <Footer />
    </div>);

};

export default CoursesPage;